import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Trophy, Calendar, Star, MessageSquare } from 'lucide-react';
import * as Icons from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { stats, features, courses, testimonials } from '../mock/data';

const Home = () => {
  return (
    <div className="min-h-screen bg-offwhite text-navy">
      {/* Hero Section */}
      <section className="pt-28 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="inline-block">
                <span className="inline-flex items-center px-4 py-2 rounded-full bg-offwhite-dark border border-navy/10 text-navy text-sm font-medium">
                  <span className="w-2 h-2 bg-electric rounded-full mr-2"></span>
                  18 Years of Excellence
                </span>
              </div>
              <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold text-navy leading-tight">
                Best Chemistry
                <span className="block text-electric">
                  tuition
                </span>
                in your area
              </h1>
              <p className="text-xl text-navy/70 leading-relaxed">
                KCC provides offline / online chemistry classes and artificial intelligence based practice platform to enhance fundamental CONCEPT OF CHEMISTRY learning by using modern teaching techniques.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Link to="/courses" className="w-full sm:w-auto">
                  <Button className="w-full bg-electric hover:bg-electric-dark text-offwhite px-6 py-4 sm:px-8 sm:py-6 text-base sm:text-lg rounded-full">
                    Start here 
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/mentors" className="w-full sm:w-auto">
                  <Button variant="outline" className="w-full border-2 border-navy/20 text-navy hover:bg-navy/5 px-6 py-4 sm:px-8 sm:py-6 text-base sm:text-lg rounded-full">
                    Learn more
                  </Button>
                </Link>
              </div>
              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-3 pt-4">
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-navy">10k+</div>
                  <div className="text-xs sm:text-sm text-navy/60">Students</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-navy">95%</div>
                  <div className="text-xs sm:text-sm text-navy/60">Success Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-navy">500+</div>
                  <div className="text-xs sm:text-sm text-navy/60">Top Ranks</div>
                </div>
              </div>
            </div>

            {/* Right Video */}
            <div className="space-y-4">
              <Card className="bg-white border border-navy/10 p-4 overflow-hidden">
                <div className="aspect-video rounded-xl overflow-hidden">
                  <iframe
                    className="w-full h-full"
                    src="https://www.youtube.com/embed/7JNrmpgk9pk"
                    title="Koley Chemistry Classes"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </Card>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card className="bg-white border border-navy/10 p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-offwhite-dark flex items-center justify-center">
                      <Trophy className="w-5 h-5 text-electric" />
                    </div>
                    <div>
                      <div className="text-navy font-semibold">Top Rankers</div>
                      <div className="text-navy/60 text-sm">Every Year</div>
                    </div>
                  </div>
                </Card>
                <Card className="bg-white border border-navy/10 p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-offwhite-dark flex items-center justify-center">
                      <Users className="w-5 h-5 text-electric" />
                    </div>
                    <div>
                      <div className="text-navy font-semibold">2000+ / 400+</div>
                      <div className="text-navy/60 text-sm">Engg / Medical Aspirants</div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon === 'users' ? Users : stat.icon === 'trophy' ? Trophy : Calendar;
              return (
                <Card
                  key={stat.id}
                  className="bg-white border border-navy/10 p-8 text-center"
                >
                  <div className="flex justify-center mb-4">
                    <div className="w-14 h-14 rounded-xl bg-offwhite-dark flex items-center justify-center">
                      <IconComponent className="w-7 h-7 text-electric" />
                    </div>
                  </div>
                  <h3 className="text-4xl font-bold text-navy mb-2">{stat.value}</h3>
                  <p className="text-navy/70">{stat.label}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-navy mb-4">
              Why parents prefer KCC
            </h2>
            <p className="text-xl text-navy/70">
              Browse all the categories of courses available at our website.
            </p>
            <Link to="/courses">
              <Button variant="outline" className="mt-6 border border-navy/20 text-navy hover:bg-navy/5">
                All Category
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const IconComponent = Icons[feature.icon];
              return (
                <Card
                  key={feature.id}
                  className="bg-white border border-navy/10 p-6 transition-colors duration-200 hover:border-navy/20"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-offwhite-dark flex items-center justify-center">
                      <IconComponent className="w-6 h-6 text-electric" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-navy mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-navy/70 text-sm leading-relaxed">{feature.description}</p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-navy mb-4">
              Popular courses for you
            </h2>
            <p className="text-xl text-navy/70">
              Get access to all the popular courses on this website.
            </p>
            <Link to="/courses">
              <Button className="mt-6 bg-electric hover:bg-electric-dark text-offwhite">
                See all courses
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((course, index) => (
              <Card
                key={course.id}
                className="bg-white border border-navy/10 overflow-hidden transition-colors duration-200 hover:border-navy/20"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={course.image}
                    alt={course.title}
                    className="w-full h-48 object-cover"
                  />
                </div>
                <div className="p-6">
                  <span className="text-navy/70 text-sm font-medium">{course.category}</span>
                  <h3 className="text-xl font-semibold text-navy mt-2 mb-4">{course.title}</h3>
                  <div className="flex items-center justify-between text-sm text-navy/70 mb-4">
                    <div className="flex items-center space-x-1">
                      <Users className="w-4 h-4" />
                      <span>{course.students}+</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                      <span>{course.rating}</span>
                    </div>
                    {/* Pricing removed */}
                  </div>
                  <Link to={`/courses/${course.id}`}>
                    <Button className="w-full bg-electric/10 hover:bg-electric/15 text-navy border border-navy/10">
                      View Details
                    </Button>
                  </Link>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-navy mb-4">
              What our students say about us
            </h2>
            <p className="text-xl text-navy/70 max-w-4xl mx-auto">
              At the heart of every successful journey lies exceptional guidance. Our students have consistently excelled in competitive exams like JEE Advanced and NEET.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.slice(0, 6).map((testimonial, index) => (
              <Card
                key={testimonial.id}
                className="bg-white border border-navy/10 p-6 transition-colors duration-200 hover:border-navy/20 relative overflow-hidden"
              >
                <div className="relative">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-14 h-14 rounded-full bg-offwhite-dark flex items-center justify-center">
                      <span className="text-navy font-bold text-lg">{testimonial.initials}</span>
                    </div>
                    <div>
                      <h4 className="text-navy font-semibold">{testimonial.name}</h4>
                      <p className="text-navy/60 text-sm">{testimonial.institution}</p>
                    </div>
                  </div>
                  <div className="flex mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${
                          i < testimonial.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600'
                        }`}
                      />
                    ))}
                  </div>
                  <h5 className="text-electric font-semibold mb-3 text-sm">{testimonial.title}</h5>
                  <p className="text-navy/70 text-sm leading-relaxed line-clamp-4">{testimonial.content}</p>

                  <div className="absolute top-4 right-4 text-6xl text-navy/10 font-serif">"</div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-offwhite-dark">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-navy border border-navy/20 p-8 md:p-12">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-offwhite/10 flex items-center justify-center mx-auto mb-6">
                <MessageSquare className="w-10 h-10 text-electric" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Ready to <span className="text-electric">start learning</span>?
              </h2>
              <p className="text-offwhite/80 mb-8 text-lg">
                Leave us your query on our WhatsApp account and we will reach out to you within 24 hours.
              </p>
              <a
                href="https://api.whatsapp.com/send?phone=7595888451"
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button className="w-full max-w-full bg-electric hover:bg-electric-dark text-offwhite px-4 py-3 text-sm sm:px-6 sm:py-6 sm:text-lg rounded-full whitespace-normal text-center leading-snug">
                  <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>Send Message on WhatsApp</span>
                </Button>
              </a>
              <p className="text-offwhite/70 text-sm mt-4">
                <span className="inline-flex items-center">
                  <span className="w-2 h-2 bg-teal rounded-full mr-2"></span>
                  Usually responds within minutes
                </span>
              </p>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Home;
